import apiClient from './api';
import { BoatInfo, SaveBoatData } from '../types';

/**
 * Service module for boat info API operations.
 * Requirements: Boat Info 2, 3, 4, 6
 */

// Helper to convert snake_case API response to camelCase
const toCamelCase = (data: Record<string, unknown>): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      result[camelKey] = toCamelCase(value as Record<string, unknown>);
    } else {
      result[camelKey] = value;
    }
  }
  return result;
};

// Helper to convert camelCase to snake_case for API requests
const toSnakeCase = (data: Record<string, unknown>): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      result[snakeKey] = toSnakeCase(value as Record<string, unknown>);
    } else {
      result[snakeKey] = value;
    }
  }
  return result;
};

export const boatService = {
  /**
   * Fetch the current user's boat information.
   * Returns null if no boat info exists.
   * Requirements: Boat Info 2.1, 2.2, 6.1
   */
  getBoat: async (): Promise<BoatInfo | null> => {
    try {
      const response = await apiClient.get<{ boat: Record<string, unknown> }>('/boat');
      const rawData = response.data.boat;
      return toCamelCase(rawData) as unknown as BoatInfo;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Create or update the current user's boat information.
   * Requirements: Boat Info 3.1-3.5, 4.1-4.4, 6.2
   */
  saveBoat: async (data: SaveBoatData): Promise<BoatInfo> => {
    const snakeData = toSnakeCase(data as unknown as Record<string, unknown>);
    const response = await apiClient.put<{ boat: Record<string, unknown> }>('/boat', snakeData);
    const rawData = response.data.boat;
    return toCamelCase(rawData) as unknown as BoatInfo;
  },
};
